/** Inline images in article HTML (base64 data URLs or safe remote URLs). */

const BASE_STYLE = "max-width:100%;border-radius:8px;margin:10px 0;display:block;";
const IMAGE_EXT = /\.(webp|avif|png|jpe?g|gif|svg|bmp|ico)(\?.*)?$/i;
const MAX_EMBED_BYTES = 1_250_000;
const MAX_EMBED_DIMENSION = 1800;

/** Explicit webp — some browsers omit MIME on pick/paste. */
export const IMAGE_ACCEPT = "image/*,.webp,.avif,image/webp,image/avif";

export function newImageId() {
  return `img-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function escapeAttr(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function buildInlineImgTag(src, alt = "Imagen", { id, zoom = 100 } = {}) {
  const imgId = id || newImageId();
  const z = Math.min(200, Math.max(25, Number(zoom) || 100));
  return `<img data-img-id="${escapeAttr(imgId)}" data-zoom="${z}" src="${escapeAttr(src)}" alt="${escapeAttr(alt)}" contenteditable="false" style="width:${z}%;${BASE_STYLE}" />`;
}

export function insertAtCursor(text, insertion, start, end) {
  return text.substring(0, start) + insertion + text.substring(end);
}

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Keep several pasted photos from exploding editor/localStorage payloads.
 * Large raster images are downscaled/re-encoded when the browser supports it;
 * GIF/SVG and unsupported browsers safely fall back to the original file.
 */
export async function readImageFileForEmbed(file) {
  if (!file) return "";
  const type = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  const preserveOriginal = type.includes("gif") || type.includes("svg") || /\.(gif|svg)$/.test(name);

  if (
    preserveOriginal ||
    !file.size ||
    file.size <= MAX_EMBED_BYTES ||
    typeof document === "undefined" ||
    typeof createImageBitmap !== "function"
  ) {
    return readFileAsDataUrl(file);
  }

  let bitmap;
  try {
    bitmap = await createImageBitmap(file);
    const longest = Math.max(bitmap.width || 1, bitmap.height || 1);
    const scale = Math.min(1, MAX_EMBED_DIMENSION / longest);
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) return readFileAsDataUrl(file);
    ctx.drawImage(bitmap, 0, 0, width, height);

    const optimized = await canvasToBlob(canvas, "image/webp", 0.82);
    if (!optimized || (file.size && optimized.size >= file.size)) {
      return readFileAsDataUrl(file);
    }
    return readFileAsDataUrl(optimized);
  } catch (error) {
    console.warn("[inlineImages] image optimization failed; using original", error);
    return readFileAsDataUrl(file);
  } finally {
    try { bitmap?.close?.(); } catch (_e) {}
  }
}

export function isImageFile(file) {
  if (!file) return false;
  if (typeof file.type === "string" && file.type.startsWith("image/")) return true;
  const name = file.name || "";
  return IMAGE_EXT.test(name);
}

export function extractImageFilesFromClipboard(clipboardData) {
  if (!clipboardData) return [];
  const seen = new Set();
  const out = [];
  const add = (file) => {
    if (!isImageFile(file)) return;
    const key = `${file.name || "paste"}-${file.size}-${file.lastModified}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(file);
  };
  if (clipboardData.files?.length) Array.from(clipboardData.files).forEach(add);
  if (clipboardData.items) {
    Array.from(clipboardData.items).forEach((item) => {
      if (item.type?.startsWith("image/")) add(item.getAsFile());
    });
  }
  return out;
}

export function extractImageFilesFromFileList(fileList) {
  return Array.from(fileList || []).filter(isImageFile);
}

export async function buildInlineImgTagListFromFiles(files) {
  const safeFiles = Array.from(files || []).filter(isImageFile);
  const results = await Promise.all(
    safeFiles.map(async (file, index) => {
      try {
        const src = await readImageFileForEmbed(file);
        const alt = safeFiles.length > 1 ? `Imagen ${index + 1}` : "Imagen";
        return src ? buildInlineImgTag(src, alt) : "";
      } catch (error) {
        console.warn("[inlineImages] skipped unreadable image", error);
        return "";
      }
    }),
  );
  return results.filter(Boolean);
}

export async function buildInlineImgTagsFromFiles(files) {
  return (await buildInlineImgTagListFromFiles(files)).join("\n");
}

export async function insertImagesIntoText(text, files, selectionStart, selectionEnd) {
  if (!files.length) return { text, cursor: selectionStart };
  const insertion = await buildInlineImgTagsFromFiles(files);
  return {
    text: insertAtCursor(text, insertion, selectionStart, selectionEnd),
    cursor: selectionStart + insertion.length,
  };
}

/** Parse img nodes from an HTML string (browser only). */
export function listImagesInHtml(html) {
  if (typeof document === "undefined") return [];
  const div = document.createElement("div");
  div.innerHTML = html || "";
  return Array.from(div.querySelectorAll("img[data-img-id], img")).map((img, i) => ({
    id: img.getAttribute("data-img-id") || `legacy-${i}`,
    src: img.getAttribute("src"),
    zoom: Number(img.getAttribute("data-zoom")) || 100,
    alt: img.getAttribute("alt") || "Imagen",
  }));
}

export function setImageZoomInHtml(html, imgId, zoom) {
  if (typeof document === "undefined") return html;
  const div = document.createElement("div");
  div.innerHTML = html || "";
  const img = div.querySelector(`img[data-img-id="${imgId}"]`);
  if (!img) return html;
  const z = Math.min(200, Math.max(25, zoom));
  img.setAttribute("data-zoom", String(z));
  img.style.width = `${z}%`;
  return div.innerHTML;
}

export function removeImageFromHtml(html, imgId) {
  if (typeof document === "undefined") return html;
  const div = document.createElement("div");
  div.innerHTML = html || "";
  div.querySelector(`img[data-img-id="${imgId}"]`)?.remove();
  return div.innerHTML;
}

export function ensureImageIds(html) {
  if (typeof document === "undefined") return html;
  const div = document.createElement("div");
  div.innerHTML = html || "";
  div.querySelectorAll("img").forEach((img) => {
    if (!img.getAttribute("data-img-id")) {
      img.setAttribute("data-img-id", newImageId());
    }
    if (!img.getAttribute("data-zoom")) img.setAttribute("data-zoom", "100");
    img.setAttribute("contenteditable", "false");
  });
  return div.innerHTML;
}